import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';

import { Button, Input } from 'src/UIComponents';
import { createSomething } from 'src/api-services';

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

function SomethingAdd({ placeholder, onClose, ...rest }) {
  const [title, setTitle] = useState('');
  const inputRef = useRef();
  const [focused, setFocused] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(createSomething, {
    onSuccess: ({ data: createdItem }) => {
      setTitle('');
      onClose();
      queryClient.invalidateQueries(['somethings']);
      navigate(`/somethings/${createdItem.id}`);
    },
  });

  const doSave = useCallback(() => {
    if (isLoading || title.trim() === '') {
      inputRef.current.focus();
      return;
    }
    mutate({
      title,
    });
  }, [mutate, isLoading, title]);

  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => setFocused(false), []);

  const handleOnChange = useCallback(
    (event) => {
      setTitle(event.target.value);
    },
    [setTitle]
  );

  const handleKeyPress = useCallback(
    (event) => {
      const char = event.which || event.charCode;
      switch (char) {
        case 13: // Enter
          doSave();
          break;
        case 27: // Escape
          setTitle('');
          event.target.blur();
          onClose();
          break;
        default:
          break;
      }
    },
    [doSave, onClose]
  );

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (focused) {
      document.addEventListener('keydown', handleKeyPress, false);
    }

    return () => document.removeEventListener('keydown', handleKeyPress, false);
  }, [focused, handleKeyPress]);

  return (
    <div {...rest}>
      <InputContainer>
        <Input
          ref={inputRef}
          value={title}
          block
          placeholder={placeholder}
          onChange={handleOnChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={handleKeyPress}
        />
        <Button onClick={doSave} disabled={isLoading || title.trim() === ''}>
          Add
        </Button>
      </InputContainer>
    </div>
  );
}

export default SomethingAdd;
